import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as assert from "typed-assert";
import debug from "debug";
import { v4 as uuidv4 } from "uuid";

import { FormConfig } from "@evergiving/common/dist/es2015/form";
import {
  Amount,
  FormSubmissionV1,
  DonationIntent,
  Donor,
  PaymentFrequency,
  PaymentSource,
  DeferredSubmissionResponse,
  ServerSubmissionResponse,
  FinalSubmissionResponse,
  SubmissionResult,
  SubmissionState,
  FormSubmissionPayload,
} from "@evergiving/common/dist/es2015/pledge";
import { wait } from "@evergiving/common/dist/es2015/utils";
import { RootState } from "@/lib/redux/types";
import { amountSelector } from "@/lib/misc/amountSelector";
import { getLanguageFromLocale } from "@/lib/config/localeUtils";

import type Stripe from "@stripe/stripe-js";

const log = debug(__filename);

const sessionUuid = uuidv4();

const clientBuildId = process.env.NEXT_BUILD_ID;
const formVersionToken = process.env.DIGITAL_FORM_VERSION_TOKEN;
const environment = process.env.EVERGIVING_ENVIRONMENT;

type StripeLocale = Stripe.StripeElementLocale | Stripe.CheckoutLocale;

function bestStripeLocale(locale: string): StripeLocale {
  const stripeLocales = new Set<StripeLocale>([
    "bg",
    "cs",
    "da",
    "de",
    "el",
    "en",
    "en-GB",
    "es",
    "es-419",
    "et",
    "fi",
    "fr",
    "fr-CA",
    "hu",
    "id",
    "it",
    "ja",
    "lt",
    "lv",
    "ms",
    "mt",
    "nb",
    "nl",
    "pl",
    "pt",
    "pt-BR",
    "ro",
    "ru",
    "sk",
    "sl",
    "sv",
    "tr",
    "zh",
    "zh-HK",
    "zh-TW",
  ]);

  if (stripeLocales.has(locale as StripeLocale)) {
    return locale as StripeLocale;
  }

  const lang = getLanguageFromLocale(locale);

  if (stripeLocales.has(lang as StripeLocale)) {
    return lang as StripeLocale;
  }

  return "auto";
}
enum SubmissionClientErrorReason {
  NetworkError,
  InvalidRequest,
  InvalidServerResponse,
  InvalidServerResponseBody,
  StripeActionUnavailable,
}

interface SubmissionClientError {
  result: "clientError";
  reason: SubmissionClientErrorReason;
  detail?: Error | number;
}

interface SubmissionTimeoutError {
  result: "timeoutError";
}

function assertServerSubmissionResponse(
  input: unknown
): asserts input is ServerSubmissionResponse {
  // FIXME
}

function serializeDonationIntent(
  amount: Amount,
  frequency: PaymentFrequency,
  startDate: string | undefined,
  paymentDay: number | undefined,
  giftAidConsent: true | false | "non-taxpayer"
) {
  const donationIntent: DonationIntent = {
    amount,
    frequency,
    ...(startDate === undefined ? {} : { startDate }),
    ...(giftAidConsent === undefined ? {} : { giftAidConsent }),
    paymentDay,
  };
  return Buffer.from(JSON.stringify(donationIntent), "utf-8").toString(
    "base64"
  );
}

async function fetchDeferredResponse({
  ablyClientKey,
  ablyChannel,
}: {
  ablyClientKey: string;
  ablyChannel: string;
}): Promise<DeferredSubmissionResponse | SubmissionTimeoutError> {
  const Ably = await import("ably/promises");
  const client = new Ably.Realtime({
    key: ablyClientKey,
  });
  try {
    const channel = client.channels.get(ablyChannel, {
      params: { rewind: String(1) },
      cipher: undefined,
      modes: ["SUBSCRIBE"],
    });
    const responsePromise = new Promise<DeferredSubmissionResponse>(
      (resolve, reject) => {
        channel.subscribe((message) => {
          try {
            const payload = JSON.parse(message.data);
            log("Received message from Ably: ", payload);
            // FIXME: validate JSON schema
            resolve(payload as DeferredSubmissionResponse);
          } catch (e) {
            reject(e);
          }
        });
      }
    );
    const timeoutPromise = wait<SubmissionTimeoutError>(12_000, {
      result: "timeoutError",
    });
    return await Promise.race([responsePromise, timeoutPromise]);
  } finally {
    client.close();
  }
}

async function fetchSubmissionResponse(
  submissionEndpointUrl: string,
  body: FormData
): Promise<
  DeferredSubmissionResponse | SubmissionClientError | SubmissionTimeoutError
> {
  let response;
  try {
    response = await fetch(submissionEndpointUrl, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      credentials: "omit",
      body,
    });
  } catch (e) {
    return {
      result: "clientError",
      reason: SubmissionClientErrorReason.InvalidRequest,
      detail: e,
    };
  }

  if (!response.ok && response.status !== 400) {
    return {
      result: "clientError",
      reason: SubmissionClientErrorReason.InvalidServerResponse,
      detail: response.status,
    };
  }

  let responseBody;
  try {
    responseBody = await response.json();
  } catch (e) {
    return {
      result: "clientError",
      reason: SubmissionClientErrorReason.InvalidServerResponseBody,
      detail: e,
    };
  }

  try {
    assertServerSubmissionResponse(responseBody);
  } catch (e) {
    return {
      result: "clientError",
      reason: SubmissionClientErrorReason.InvalidServerResponseBody,
      detail: e,
    };
  }

  if (responseBody.result === SubmissionResult.Deferred) {
    log("Deferred result!", responseBody);
    return fetchDeferredResponse(responseBody);
  }
  return responseBody;
}

export type FormSubmitFunction = (
  donor: Donor,
  paymentSource?: PaymentSource,
  submissionState?: SubmissionState
) => Promise<
  FinalSubmissionResponse | SubmissionClientError | SubmissionTimeoutError
>;

export function useFormSubmission(
  formConfig: FormConfig
): {
  submit: FormSubmitFunction;
  donationIntentBase64?: string;
} {
  const previousSubmissionState = useSelector(
    (state: RootState) => state.previousSubmissionState
  );
  const testingReference = useSelector(
    (state: RootState) => state.testingReference
  );
  const formSourceRef = useSelector((state: RootState) => state.formSourceRef);
  log("useFormSubmission, formSourceRef=", formSourceRef);

  const amount = useSelector(amountSelector);
  const frequency = useSelector((state: RootState) => state.paymentFrequency);
  const giftAidConsent = useSelector(
    (state: RootState) => state.giftAidConsent
  );
  const startDate = useSelector((state: RootState) => state.startDate);
  const paymentDay = useSelector((state: RootState) => state.paymentDay);

  const [donationIntentBase64, setDonationIntentBase64] = useState<string>(
    "payment" in formConfig && frequency !== undefined
      ? serializeDonationIntent(
          amount,
          frequency,
          startDate,
          paymentDay,
          giftAidConsent
        )
      : ""
  );

  useEffect(() => {
    if ("payment" in formConfig) {
      assert.isNotUndefined(frequency);
      setDonationIntentBase64(
        serializeDonationIntent(
          amount,
          frequency,
          startDate,
          paymentDay,
          giftAidConsent
        )
      );
    }
  }, [amount, frequency, startDate, giftAidConsent, paymentDay]);

  const { campaignUuid } = formConfig;

  const submit = async (
    donor: Donor,
    paymentSource?: PaymentSource,
    submissionState?: SubmissionState
  ): Promise<
    FinalSubmissionResponse | SubmissionClientError | SubmissionTimeoutError
  > => {
    const payload: FormSubmissionPayload =
      paymentSource !== undefined && donationIntentBase64 !== ""
        ? {
            donationIntentBase64,
            donor,
            paymentSource,
          }
        : {
            donor,
          };

    assert.isNotUndefined(clientBuildId);
    assert.isNotUndefined(environment);

    const formSubmission: FormSubmissionV1 = {
      version: 1,
      metadata: {
        campaignUuid,
        sessionUuid,
        testingReference,
        formVersionToken,
        clientBuildId,
        environment,
        formSourceRef,
      },
      previousSubmissionState: submissionState ?? previousSubmissionState,
      payload,
    };

    const body = new FormData();
    body.append(
      "blob",
      new Blob([JSON.stringify(formSubmission)], {
        type: "application/json",
      })
    );

    const submissionResponse = await fetchSubmissionResponse(
      formConfig.submissionEndpointUrl,
      body
    );

    if (submissionResponse.result === SubmissionResult.StripeRequiresAction) {
      const { loadStripe } = await import("@stripe/stripe-js");
      const stripe = await loadStripe(submissionResponse.clientApiKey, {
        locale: bestStripeLocale(formConfig.locale),
      });
      if (!stripe) {
        return {
          result: "clientError",
          reason: SubmissionClientErrorReason.StripeActionUnavailable,
        };
      }
      const result = await stripe.confirmCardPayment(
        submissionResponse.clientSecret
      );
      if (result.error) {
        // FIXME
        console.warn("handleCardAction got error: ", result.error);
        return {
          result: "clientError",
          reason: SubmissionClientErrorReason.StripeActionUnavailable, // FIXME
        };
      }

      const { paymentIntent } = result;
      if (paymentIntent?.status === "succeeded") {
        return submit(donor, paymentSource, {
          ...submissionResponse.state,
          paymentIntentId: paymentIntent.id,
        });
      }

      return {
        result: "clientError",
        reason: SubmissionClientErrorReason.StripeActionUnavailable, // FIXME
      };
    }

    return submissionResponse;
  };

  return donationIntentBase64 !== ""
    ? { submit, donationIntentBase64 }
    : { submit };
}
